import React, { useState, useCallback } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { Loader } from '@els/els-react--loader';
import useHandleableViewportResize from '@Hooks/useHandleableViewportResize';
import {
  recordCountOrResultSizeFromHistoryItem,
  recordCountOrTotalResultFromHistoryItem,
} from '@Containers/PatentFamilyClustering/selectors';
import RsPaging from '@Pages/RetroSynthesis/components/RsPaging';
import { isAnonymousUser } from '@Pages/Login/LoginSelectors';
import { capitalizeFirstLetter, getHistoryDisplayTime } from './utils';
import HistoryBanner from './components/HistoryBanner';
import HistoryToolbar from './components/HistoryToolbar';
import HistoryListItem from './components/HistoryListItem';
import useHistoryData from './components/hooks/useHistoryData';
import HistoryEmptyState from './components/HistoryEmptyState/HistoryEmptyState';

import './AutoSave.scss';

const BANNER_DISMISSED_SESSION_KEY = 'history-recent-banner-dismissed';

function AutoSave({ page }) {
  const [showBanner, setShowBanner] = useState(() => sessionStorage.getItem(BANNER_DISMISSED_SESSION_KEY) !== 'true');
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const isAnonymous = isAnonymousUser();

  useHandleableViewportResize(useCallback(() => setIsMobileView(window.innerWidth <= 768), []));

  const {
    historyTypes,
    isLoaded,
    currentPage,
    pageSize,
    totalItems,
    searchInputText,
    paginatedItems,
    allSelectedOnPage,
    groupedPaginatedItems,
    dateGroupedPaginatedItems,
    handlePageChange,
  } = useHistoryData(page, isAnonymous);
  const hasGroupedItems = page === 'recent' ? paginatedItems.length > 0 : Object.keys(groupedPaginatedItems || {}).length > 0;
  const hasActiveFilter = Boolean(searchInputText?.trim());

  const getRecordCount = (item) =>
    page === 'saved' ? recordCountOrResultSizeFromHistoryItem(item) : recordCountOrTotalResultFromHistoryItem(item);
  const getItemTime = (item) => getHistoryDisplayTime(item) ?? item?.creationTime ?? item?.userTime;
  const getGroupHeadingId = (groupType, groupLabel) => {
    const normalizedLabel = String(groupLabel || 'group')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    return `history-${page}-${groupType}-${normalizedLabel || 'group'}`;
  };

  if (!isLoaded) {
    return (
      <div className="history-page__container" aria-busy="true" aria-label="Loading history page">
        <Loader isVisible isBlocking />
      </div>
    );
  }

  return (
    <div className="history-page__container auto-save__container">
      {page === 'recent' && (
        <HistoryBanner
          isVisible={showBanner}
          isAnonymous={isAnonymous}
          onClose={() => {
            sessionStorage.setItem(BANNER_DISMISSED_SESSION_KEY, 'true');
            setShowBanner(false);
          }}
        />
      )}
      {(hasGroupedItems || hasActiveFilter) && (
        <section className={cn({ 'u-els-margin-top': showBanner && page === 'recent' })}>
          <HistoryToolbar page={page} paginatedItems={paginatedItems} allSelectedOnPage={allSelectedOnPage} />
        </section>
      )}
      <div className="auto-save__history-body">
        {hasGroupedItems ? (
          <ul className="auto-save__history-list" aria-label="History search results">
            {page === 'recent'
              ? Object.entries(dateGroupedPaginatedItems || {})?.map(([dateLabel, items]) => (
                  <li key={`${page}DateGroup${dateLabel}`}>
                    <h2 id={getGroupHeadingId('date', dateLabel)} className="u-els-font-size-h4 u-els-margin-left-2x u-els-margin-top">
                      {dateLabel}
                    </h2>
                    <ul className="auto-save__history-group" aria-labelledby={getGroupHeadingId('date', dateLabel)}>
                      {items.map(({ item, groupItemIndex, itemIndex, groupItem }) => (
                        <HistoryListItem
                          key={`${page}RecentItem${item?.resultName || itemIndex}`}
                          item={item}
                          name={item?.searchName}
                          type={item?.context ? capitalizeFirstLetter(item?.context?.replaceAll('"', '')) : ''}
                          time={getItemTime(item)}
                          database={item?.comment}
                          databaseName={item?.databaseName || groupItem.databaseName}
                          recordCount={getRecordCount(item)}
                          page={page}
                          groupTitle={dateLabel}
                          groupItemIndex={groupItemIndex}
                          itemIndex={itemIndex}
                          historyTypes={historyTypes}
                        />
                      ))}
                    </ul>
                  </li>
                ))
              : Object.entries(groupedPaginatedItems)?.map(([databaseName, items]) => (
                  <li key={`${page}GroupItem${databaseName}`}>
                    <h2
                      id={getGroupHeadingId('database', databaseName)}
                      className="u-els-font-size-h4 u-els-margin-left-2x u-els-margin-top">
                      {databaseName}
                    </h2>
                    <ul className="auto-save__history-group" aria-labelledby={getGroupHeadingId('database', databaseName)}>
                      {items.map(({ item, groupItemIndex, itemIndex, groupItem }) => (
                        <HistoryListItem
                          key={`${page}GroupItem${groupItem.databaseName}-${item?.id || item?.resultName || itemIndex}`}
                          item={item}
                          name={item?.searchName}
                          type={item?.context ? capitalizeFirstLetter(item?.context?.replaceAll('"', '')) : ''}
                          time={getItemTime(item)}
                          database={item?.comment}
                          databaseName={groupItem.databaseName}
                          recordCount={getRecordCount(item)}
                          page={page}
                          groupItemIndex={groupItemIndex}
                          itemIndex={itemIndex}
                          historyTypes={historyTypes}
                        />
                      ))}
                    </ul>
                  </li>
                ))}
          </ul>
        ) : (
          <HistoryEmptyState page={page} source={hasActiveFilter ? 'filtered' : 'nodata'} isAnonymous={isAnonymous} />
        )}
        {Math.ceil(totalItems / pageSize) > 1 && (
          <div>
            <RsPaging
              totalItems={totalItems}
              currentPage={currentPage}
              totalPages={Math.ceil(totalItems / pageSize)}
              navigationLabel={`${capitalizeFirstLetter(page)} history pagination`}
              onChange={(pageDetails) => {
                handlePageChange(pageDetails.size, pageDetails.page + 1);
              }}
              pageSize={pageSize}
              isMobileView={isMobileView}
            />
          </div>
        )}
      </div>
    </div>
  );
}

AutoSave.propTypes = {
  page: PropTypes.string.isRequired,
};

export default AutoSave;
